/**
 * `DIALOG.textMode` (W2-06; DESIGN.md §6.8): choose the mode of a text segment and type its texts, with Phira's
 * guards explained and a live preview computed with Phira's string tween; Apply runs
 * Core.lines.setTextSegmentMode as one undoable transaction and resolves `{ applied: true }` (null on cancel).
 */

import { useCallback, useId, useMemo, useState } from 'react';
import type { AppServices, DialogProps } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { Ease } from '../../core/ease/index';
import type { ChartDoc, DocIndexes, KeyId } from '../../core/model/types';
import { formatBeat } from '../../core/time/beatSyntax';
import type { MessageRef } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { Button, Dialog, NumberField, TextArea } from '../../ui';
import { useDocValue, useServicesStore, useSettings } from './hooks';
import { TEXT_MODE_DESC, TEXT_MODE_LABEL } from './labels';
import { m } from './inspector.messages';
import { counterDecimals, counterText, counterValue, TEXT_MODES, textGuard, type TextMode } from './model';
import { Callout, Readout } from './parts';
import s from './Inspector.module.css';
import { textSegmentOf, TextPreviewStrip, type TextSegment } from './sections/TextSegmentSection';
import { runTx } from './tx';

/** Texts a mode starts with, from the segment's current strings. */
export function initialTexts(seg: TextSegment, mode: TextMode): { readonly from: string; readonly to: string } {
  switch (mode) {
    case 'switch':
      return { from: seg.start.split('%P%').join(''), to: seg.after.split('%P%').join('') };
    case 'typeOn':
      return { from: '', to: seg.end !== '' ? seg.end : seg.start };
    case 'erase':
      return { from: seg.start !== '' ? seg.start : seg.end, to: '' };
    case 'extend':
      return { from: seg.start, to: seg.end };
    case 'counter': {
      const a = counterValue(seg.start);
      const b = counterValue(seg.end);
      return { from: a === null ? '0' : counterText(a), to: b === null ? '0' : counterText(b) };
    }
  }
}

/** The strings Phira will tween between once the mode is applied (preview). */
export function plannedStrings(
  mode: TextMode,
  from: string,
  to: string,
  seg: TextSegment,
): { readonly start: string; readonly end: string; readonly after: string } {
  switch (mode) {
    case 'switch':
      return { start: from, end: from, after: to };
    case 'typeOn':
      return { start: '', end: to, after: seg.after === seg.end ? to : seg.after };
    case 'erase':
      return { start: from, end: '', after: seg.after === seg.end ? '' : seg.after };
    case 'extend':
      return { start: from, end: to, after: seg.after === seg.end ? to : seg.after };
    case 'counter':
      return { start: `%P%${from}`, end: `%P%${to}`, after: to };
  }
}

/** Why the texts cannot be applied in this mode (null = they can). */
export function textModeProblem(mode: TextMode, from: string, to: string): MessageRef | null {
  switch (mode) {
    case 'switch':
      return null;
    case 'typeOn':
      return to === '' ? { key: m.textEmpty } : null;
    case 'erase':
      return from === '' ? { key: m.textEmpty } : null;
    case 'extend':
      return from !== to && (to.startsWith(from) || from.startsWith(to)) ? null : { key: m.textExtendNeedsPrefix };
    case 'counter':
      return counterValue(from) === null || counterValue(to) === null ? { key: m.counterNotNumeric } : null;
  }
}

export function TextModeDialog(props: DialogProps<'textMode'>) {
  const store = useServicesStore(props.services);
  if (store === null) return null;
  return <TextModeBody {...props} store={store} />;
}

function TextModeBody(props: DialogProps<'textMode'> & { readonly store: DocStore }) {
  const { services, store } = props;
  const segment = props.props.segment;
  const seg = useDocValue(
    store,
    useCallback((doc: ChartDoc, idx: DocIndexes) => textSegmentOf(doc, idx, segment), [segment]),
    ['keys'],
  );
  if (seg === null) return null;
  return (
    <TextModeForm
      services={services}
      store={store}
      seg={seg}
      segment={segment}
      {...(props.props.mode !== undefined ? { mode: props.props.mode } : {})}
      close={(r) => {
        props.close(r);
      }}
    />
  );
}

function TextModeForm(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly seg: TextSegment;
  readonly segment: KeyId;
  /** Pre-selected mode (TextModeProps.mode, CR-W2-06-3). */
  readonly mode?: TextMode;
  readonly close: (result: { readonly applied: true } | null) => void;
}) {
  const { services, store, seg } = props;
  const t = useT();
  const beatStyle = useSettings(services).editing.beatDisplay;
  const groupName = useId();
  const [mode, setMode] = useState<TextMode>(props.mode ?? seg.mode);
  const [texts, setTexts] = useState(() => initialTexts(seg, props.mode ?? seg.mode));
  const choose = (next: TextMode): void => {
    setMode(next);
    setTexts(initialTexts(seg, next));
  };
  const problem = textModeProblem(mode, texts.from, texts.to);
  const planned = useMemo(() => plannedStrings(mode, texts.from, texts.to, seg), [mode, texts, seg]);
  const overshoots = Ease.overshoots(seg.key.ease);
  const guard = mode === 'switch' ? null : textGuard(mode, seg.key.ease);
  const previewEase =
    mode === 'switch'
      ? { kind: 'hold' as const }
      : guard === 'crash' || seg.key.ease.kind === 'hold'
        ? Ease.LINEAR
        : seg.key.ease;

  const apply = (): void => {
    const texts2 =
      mode === 'switch' || mode === 'extend' || mode === 'counter'
        ? { from: texts.from, to: texts.to }
        : mode === 'typeOn'
          ? { to: texts.to }
          : { from: texts.from };
    const out = runTx(services, store, { label: { key: m.txTextMode } }, (tx) => {
      Core.lines.setTextSegmentMode(tx, { segment: props.segment, mode, ...texts2 });
    });
    if (out.ok) props.close({ applied: true });
  };

  const fromField = (label: string) => (
    <TextArea
      label={label}
      value={texts.from}
      rows={2}
      onChange={(v) => {
        setTexts((x) => ({ ...x, from: v }));
      }}
      onCommit={(v) => {
        setTexts((x) => ({ ...x, from: v }));
      }}
    />
  );
  const toField = (label: string) => (
    <TextArea
      label={label}
      value={texts.to}
      rows={2}
      onChange={(v) => {
        setTexts((x) => ({ ...x, to: v }));
      }}
      onCommit={(v) => {
        setTexts((x) => ({ ...x, to: v }));
      }}
    />
  );
  const counterField = (side: 'from' | 'to') => {
    const v = counterValue(texts[side]);
    return (
      <NumberField
        label={t(side === 'from' ? m.counterFrom : m.counterTo)}
        value={v === null ? { kind: 'mixed' } : { kind: 'value', value: v }}
        step={1}
        precision={3}
        onCommit={(nv) => {
          if (nv.kind !== 'number') return;
          setTexts((x) => ({ ...x, [side]: counterText(nv.value) }));
        }}
      />
    );
  };
  const counterFrom = counterValue(texts.from);
  const counterTo = counterValue(texts.to);

  return (
    <Dialog
      open
      size="md"
      title={t(m.textModeTitle)}
      description={t(m.textModeSegment, {
        from: formatBeat(seg.key.t, beatStyle),
        to: formatBeat(seg.next.t, beatStyle),
      })}
      onClose={() => {
        props.close(null);
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              props.close(null);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button variant="primary" icon="Check" disabledReason={problem} onClick={apply}>
            {t(m.apply)}
          </Button>
        </>
      }
    >
      <div className={s.dialogBody}>
        <fieldset className={s.choiceList}>
          <legend className={s.subheading}>{t(m.textMode)}</legend>
          {TEXT_MODES.map((md) => (
            <label key={md} className={s.choice}>
              <input
                type="radio"
                name={groupName}
                value={md}
                checked={mode === md}
                onChange={() => {
                  choose(md);
                }}
              />
              <span>{t(TEXT_MODE_LABEL[md])}</span>
              <span className={s.choiceDesc}>{t(TEXT_MODE_DESC[md])}</span>
            </label>
          ))}
        </fieldset>

        <div className={s.dialogSection}>
          {mode === 'switch' && (
            <>
              {fromField(t(m.textFrom))}
              {toField(t(m.textThen))}
            </>
          )}
          {mode === 'typeOn' && toField(t(m.textTypes))}
          {mode === 'erase' && fromField(t(m.textErases))}
          {mode === 'extend' && (
            <>
              {fromField(t(m.textStart))}
              {toField(t(m.textEnd))}
            </>
          )}
          {mode === 'counter' && (
            <>
              {counterField('from')}
              {counterField('to')}
              {counterFrom !== null && counterTo !== null && (
                <Readout>{t(counterDecimals(counterFrom, counterTo) === 0 ? m.counterInt : m.counterDec)}</Readout>
              )}
            </>
          )}
        </div>

        {(mode === 'typeOn' || mode === 'erase') && overshoots && (
          <Callout tone="warning" testId="dialog-guard">
            {t(m.guardCrash)}
          </Callout>
        )}
        {mode === 'extend' && overshoots && (
          <Callout tone="warning" testId="dialog-guard">
            {t(m.guardWarn)}
          </Callout>
        )}
        {problem !== null && <Callout tone="danger">{t(problem.key)}</Callout>}
        {mode !== 'switch' && <Readout>{t(m.textSplitApprox)}</Readout>}

        <div className={s.dialogSection}>
          <span className={s.subheading}>{t(m.textPreview)}</span>
          <TextPreviewStrip start={planned.start} end={planned.end} after={planned.after} ease={previewEase} />
        </div>
      </div>
    </Dialog>
  );
}
